import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  AdvancedSettings,
  AutomationCard,
  EditButton,
  SettingGroup,
  SettingToggle,
  SettingsCard,
  ToggleAllButton,
  ValueTiles,
} from '@/components/settings/SettingsParts'
import type {
  AutomationSettings,
  DateBehaviorSettings,
  DeviationThresholds,
  ErpSyncSettings,
  LeadTimeSettings,
  ParseFormatSettings,
} from '@/features/order-confirmation/settings/orderConfirmationSettingsMappers'
import {
  OC_HEAD_COMPARISON_FIELDS,
  OC_HEAD_DELIVERY_ADDRESS_FIELDS,
  OC_HEAD_GENERAL_FIELDS,
  OC_HEAD_INVOICE_ADDRESS_FIELDS,
  type OcHeadComparisonField,
  type OcHeadComparisonFieldId,
} from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import { OC_LINE_COMPARISON_FIELDS } from '@/features/order-confirmation/settings/ocLineComparisonFields'
import type { OcLineComparisonFlagFieldId } from '@/types/orderConfirmationSettings'

/**
 * The sections of the order confirmation settings, shared by the workflow's page and a
 * supplier's. Page layout (SettingsParts): automation on top, flagging beside thresholds and
 * ERP sync, Advanced below.
 */

export function FullyAutomaticValidationSection({
  automation,
  onToggle,
}: {
  automation: AutomationSettings
  onToggle: (checked: boolean) => void
}) {
  return (
    <AutomationCard
      checked={automation.fullyAutomaticValidation}
      onChange={onToggle}
      description="Validates an order confirmation without a person looking at it, when it has no deviation according to the settings below."
      whenOn="On: confirmations without deviations are validated automatically"
      whenOff="Off: every confirmation waits for your team"
    />
  )
}

export function ErpSyncSection({
  erpSync,
  onToggle,
}: {
  erpSync: ErpSyncSettings
  onToggle: (checked: boolean) => void
}) {
  return (
    <SettingsCard title="ERP sync">
      <SettingToggle
        label="Update supplier order number on ERP sync"
        description="Writes the supplier's own order number onto the purchase order in the ERP when a confirmation is synced."
        checked={erpSync.updateSupplierOrderNumberOnErpSync}
        onChange={onToggle}
      />
    </SettingsCard>
  )
}

/** Advanced settings, in two columns. */
export function AdvancedSettingsSection({ children }: { children: React.ReactNode }) {
  return <AdvancedSettings description="Arrival dates and how documents are read. Rarely changed after setup.">{children}</AdvancedSettings>
}

function validateThresholds(
  delayDaysInput: string,
  earlyDaysInput: string,
  priceDifferenceInput: string,
): { ok: true; value: DeviationThresholds } | { ok: false; error: string } {
  const delayDays = Number(delayDaysInput)
  if (!Number.isFinite(delayDays) || !Number.isInteger(delayDays) || delayDays < 1) {
    return { ok: false, error: 'Delay days must be a whole number of 1 or higher.' }
  }

  const earlyDays = Number(earlyDaysInput)
  if (!Number.isFinite(earlyDays) || !Number.isInteger(earlyDays) || earlyDays < 1) {
    return { ok: false, error: 'Early days must be a whole number of 1 or higher.' }
  }

  const priceDifferencePercent = Number(priceDifferenceInput)
  if (!Number.isFinite(priceDifferencePercent) || priceDifferencePercent < 0) {
    return { ok: false, error: 'Price difference must be 0 or higher.' }
  }

  return { ok: true, value: { delayDays, earlyDays, priceDifferencePercent } }
}

export function DeviationThresholdsSection({
  thresholds,
  onDraftChange,
}: {
  thresholds: DeviationThresholds
  onDraftChange: (next: DeviationThresholds) => void
}) {
  const [editing, setEditing] = useState(false)
  const [delayDaysInput, setDelayDaysInput] = useState(String(thresholds.delayDays))
  const [earlyDaysInput, setEarlyDaysInput] = useState(String(thresholds.earlyDays))
  const [priceDifferenceInput, setPriceDifferenceInput] = useState(
    String(thresholds.priceDifferencePercent),
  )
  const [error, setError] = useState<string | null>(null)

  function startEdit() {
    setDelayDaysInput(String(thresholds.delayDays))
    setEarlyDaysInput(String(thresholds.earlyDays))
    setPriceDifferenceInput(String(thresholds.priceDifferencePercent))
    setError(null)
    setEditing(true)
  }

  function cancelEdit() {
    setError(null)
    setEditing(false)
  }

  function applyEdit() {
    const result = validateThresholds(delayDaysInput, earlyDaysInput, priceDifferenceInput)
    if (!result.ok) {
      setError(result.error)
      return
    }
    onDraftChange(result.value)
    setEditing(false)
    setError(null)
  }

  return (
    <SettingsCard
      title="Deviation thresholds"
      about="How far a confirmed date or price may differ from the purchase order before the confirmation is put in front of a person. A threshold of 0 flags every difference."
      description="Flagged at these values or more"
      actions={!editing && <EditButton label="Edit deviation thresholds" onClick={startEdit} />}
    >
      {editing ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Late (days)</span>
              <Input type="number" min={1} step={1} inputMode="numeric" value={delayDaysInput} onChange={(e) => setDelayDaysInput(e.target.value)} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Early (days)</span>
              <Input type="number" min={1} step={1} inputMode="numeric" value={earlyDaysInput} onChange={(e) => setEarlyDaysInput(e.target.value)} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Price (%)</span>
              <Input type="number" min={0} step={0.1} inputMode="decimal" value={priceDifferenceInput} onChange={(e) => setPriceDifferenceInput(e.target.value)} />
            </label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={applyEdit}>Apply</Button>
            <Button type="button" size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
          </div>
        </div>
      ) : (
        <ValueTiles
          items={[
            { label: 'Late', value: thresholds.delayDays, unit: 'days' },
            { label: 'Early', value: thresholds.earlyDays, unit: 'days' },
            { label: 'Price', value: thresholds.priceDifferencePercent, unit: '%' },
          ]}
        />
      )}
    </SettingsCard>
  )
}

export function DateBehaviorSection({
  dateBehavior,
  onToggle,
}: {
  dateBehavior: DateBehaviorSettings
  onToggle: (key: keyof DateBehaviorSettings, checked: boolean) => void
  nested?: boolean
}) {
  return (
    <SettingsCard
      title="Date behavior"
      description="When a confirmation has a delivery date on the order but not on its rows."
    >
      <div className="divide-y divide-gray-100">
        <SettingToggle
          label="Use the order head date only if all rows are missing dates"
          description="Recommended"
          checked={dateBehavior.useHeadDateIfAllRowsAreMissingDates}
          onChange={(checked) => onToggle('useHeadDateIfAllRowsAreMissingDates', checked)}
        />
        <SettingToggle
          label="Use the order head date on rows that are missing dates"
          checked={dateBehavior.useDeviatingDatesOnRow}
          onChange={(checked) => onToggle('useDeviatingDatesOnRow', checked)}
        />
      </div>
    </SettingsCard>
  )
}

export function ParseFormatSection({
  parseFormat,
  onToggle,
}: {
  parseFormat: ParseFormatSettings
  onToggle: (key: keyof ParseFormatSettings, checked: boolean) => void
  nested?: boolean
}) {
  const noDateFormatSelected =
    !parseFormat.dateParsingWeekFormat &&
    !parseFormat.dateParsingAmericanFormat &&
    !parseFormat.dateParsingIsoFormat &&
    !parseFormat.dateParsingDayFirstFormat

  return (
    <SettingsCard
      title="Parsing format"
      about="Turning off a format a supplier never uses stops a date being read the wrong way."
      description="Which formats a supplier's document is read with. At least one date format stays on."
    >
      <SettingGroup title="Numbers">
        <SettingToggle layout="compact" label="English number format (1,234.56)" checked={parseFormat.floatParsingEnglishFormat} onChange={(checked) => onToggle('floatParsingEnglishFormat', checked)} />
      </SettingGroup>
      <SettingGroup title="Dates" className="mt-3">
        <SettingToggle layout="compact" label="Week number (YYWWD)" checked={parseFormat.dateParsingWeekFormat} onChange={(checked) => onToggle('dateParsingWeekFormat', checked)} />
        <SettingToggle layout="compact" label="American (MM/DD/YYYY)" checked={parseFormat.dateParsingAmericanFormat} onChange={(checked) => onToggle('dateParsingAmericanFormat', checked)} />
        <SettingToggle layout="compact" label="ISO 8601 (YYYY-MM-DD)" checked={parseFormat.dateParsingIsoFormat} onChange={(checked) => onToggle('dateParsingIsoFormat', checked)} />
        <SettingToggle layout="compact" label="Day first (DD-MM-YYYY)" checked={parseFormat.dateParsingDayFirstFormat} onChange={(checked) => onToggle('dateParsingDayFirstFormat', checked)} />
      </SettingGroup>
      {noDateFormatSelected && (
        <p className="mt-3 text-sm text-red-600">At least one date format must be on.</p>
      )}
    </SettingsCard>
  )
}

export function LeadTimeSection({
  leadTime,
  onToggleUseLeadTime,
  onLeadTimeInDaysChange,
}: {
  leadTime: LeadTimeSettings
  onToggleUseLeadTime: (checked: boolean) => void
  onLeadTimeInDaysChange: (days: number) => void
  nested?: boolean
}) {
  const [leadTimeInput, setLeadTimeInput] = useState(String(leadTime.leadTimeInDays))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLeadTimeInput(String(leadTime.leadTimeInDays))
    setError(null)
  }, [leadTime.leadTimeInDays])

  function applyLeadTimeInput() {
    const days = Number(leadTimeInput)
    if (!Number.isFinite(days) || !Number.isInteger(days) || days < 0) {
      setError('Lead time must be a whole number of 0 or higher.')
      return
    }
    onLeadTimeInDaysChange(days)
    setError(null)
  }

  return (
    <SettingsCard
      title="Lead time"
      about="Adds your own handling days to the supplier's date, so the arrival compared against the purchase order is when the goods are with you rather than when they leave."
    >
      <SettingToggle
        label="Use lead time when calculating the arrival date"
        checked={leadTime.useLeadTimeWhenCalculatingArrival}
        onChange={onToggleUseLeadTime}
      />
      {leadTime.useLeadTimeWhenCalculatingArrival && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-4">
          <div className="flex gap-2.5 rounded-chip border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              If your ERP has lead time settings, it already does this. Only turn this on if you know
              exactly what you are doing: it overrides the ERP's settings.
            </p>
          </div>
          <label className="block max-w-[10rem] space-y-1.5">
            <span className="text-xs font-medium text-gray-700">Lead time (days)</span>
            <Input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={leadTimeInput}
              onChange={(e) => setLeadTimeInput(e.target.value)}
              onBlur={applyLeadTimeInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyLeadTimeInput()
                }
              }}
            />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </SettingsCard>
  )
}

export function FlagDifferencesSection({
  flaggedFields,
  onToggle,
  onToggleAll,
  flaggedLineFields,
  onToggleLine,
  onToggleAllLine,
}: {
  flaggedFields: Set<OcHeadComparisonFieldId>
  onToggle: (id: OcHeadComparisonFieldId, checked: boolean) => void
  onToggleAll: (checked: boolean) => void
  flaggedLineFields: Set<OcLineComparisonFlagFieldId>
  onToggleLine: (id: OcLineComparisonFlagFieldId, checked: boolean) => void
  onToggleAllLine: (checked: boolean) => void
}) {
  const allHeadOn = flaggedFields.size === OC_HEAD_COMPARISON_FIELDS.length
  const allLineOn = flaggedLineFields.size === OC_LINE_COMPARISON_FIELDS.length
  const flaggedCount = flaggedFields.size + flaggedLineFields.size
  const totalCount = OC_HEAD_COMPARISON_FIELDS.length + OC_LINE_COMPARISON_FIELDS.length

  const headToggles = (fields: OcHeadComparisonField[]) =>
    fields.map((field) => (
      <SettingToggle
        key={field.id}
        layout="compact"
        label={field.label}
        checked={flaggedFields.has(field.id)}
        onChange={(checked) => onToggle(field.id, checked)}
      />
    ))

  return (
    <SettingsCard
      title="Flag differences in"
      about="Which fields, when the confirmation says something other than the purchase order, should stop the confirmation for review."
      description={`${flaggedCount} of ${totalCount} fields are flagged`}
    >
      <div className="space-y-5">
        <SettingGroup title="Order head" action={<ToggleAllButton allOn={allHeadOn} onToggle={onToggleAll} what="order head fields" />}>
          <div className="grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3">{headToggles(OC_HEAD_GENERAL_FIELDS)}</div>
          <div className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <SettingGroup title="Invoice address">{headToggles(OC_HEAD_INVOICE_ADDRESS_FIELDS)}</SettingGroup>
            <SettingGroup title="Delivery address">{headToggles(OC_HEAD_DELIVERY_ADDRESS_FIELDS)}</SettingGroup>
          </div>
        </SettingGroup>
        <SettingGroup
          title="Rows"
          action={<ToggleAllButton allOn={allLineOn} onToggle={onToggleAllLine} what="row fields" />}
          className="border-t border-gray-100 pt-4"
        >
          <div className="grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
            {OC_LINE_COMPARISON_FIELDS.map((field) => (
              <SettingToggle
                key={field.id}
                layout="compact"
                label={field.label}
                checked={flaggedLineFields.has(field.id)}
                onChange={(checked) => onToggleLine(field.id, checked)}
              />
            ))}
          </div>
        </SettingGroup>
      </div>
    </SettingsCard>
  )
}
